import React, { SVGProps, useMemo, useState } from 'react';
import { getSemanticColor, SemanticCategoryMap } from '@athena/forge-shared';
import createBEMHelper from './bem-helper';
import { nanoid } from 'nanoid';

/** The available semantic color categories whitelisted for use on an icon */
const iconColorCategories = ['alert', 'font', 'interaction'] as const;
/** The type representing a whitelisted semantic color category */
export type IconColorCategory = typeof iconColorCategories[number];
/** The type representing a valid name within a semantic color category. */
export type IconColorName<Category extends IconColorCategory> = keyof SemanticCategoryMap[Category];

/** An Object representing a semantic color category and name within that category. */
export interface IconColor<Category extends IconColorCategory> {
  category: Category;
  name: IconColorName<Category>;
}

export type Variant = 'default' | 'interactive' | 'disabled' | 'inverted';

/** The props available to every exported forge icon */
export interface IconProps<Category extends IconColorCategory> extends SVGProps<SVGSVGElement> {
  /** @deprecated: Use `variant='disabled'` instead
   *
   * Used to disable an icon whose interactive has been set to true.
   * Setting this flag true or false will have no effect unless interactive
   * is set to true.
   */
  disabled?: boolean;
  /** @deprecated: Use `variant='interactive'` instead
   *
   * Denotes if an icon can be interactive. If false or undefined, the icon
   * takes on the default color (#373738). If true, the icon takes on
   * blue-default (#0466B4) color, unless disabled is also true.
   */
  interactive?: boolean;
  /** Sets the size of the icon (a shortcut for setting height and width) */
  size?: string | number;
  /** Recolors the icon convey a specific intention.
   *
   * 'default':    For non-interactive icons on light backgrounds
   * 'interactive' For interactive icons on light backgrounds
   * 'disabled'    For disabled icons on light backgrounds
   * 'inverted'    For icons on dark backgrounds
   */
  variant?: Variant;
  /** Recolors the icon based on a semantic color category and name.
   *
   * Overrides `variant`.
   */
  semanticColor?: IconColor<Category>;
  /** Sets the inline content of the `<title>` element in the SVG (which is read when a screen
   * reader encounters the icon); defaults to the icon's name */
  title?: string;
}

export type IconSize = 'small' | 'large';

/** Represents a raw component created by svgr */
interface SVGRProps {
  title?: string;
  titleId?: string;
}
export interface SvgComponentType {
  (props: React.SVGProps<SVGSVGElement> & SVGRProps): JSX.Element;
  displayName?: string;
  size?: string;
}

const colorInteractive = getSemanticColor('interaction', 'default');
const colorDisabled = getSemanticColor('interaction', 'disabled');
const colorDefault = getSemanticColor('font', 'default');
const colorInverted = getSemanticColor('font', 'invert');

export interface UseColorArgs<Category extends IconColorCategory> {
  variant: Variant;
  semanticColor?: IconColor<Category>;
}

/** Determines what color the icon should be.
 *
 * The semanticColor prop takes precedence if it's defined.
 * Any further coloring must have the interactive prop set to true.
 * The final color is also determined by the disabled prop.
 * If neither semanticColor nor interactive are defined then the default color is used.
 */
export function useColor<Category extends IconColorCategory>({
  variant,
  semanticColor,
}: UseColorArgs<Category>): string {
  if (semanticColor) {
    const { category, name } = semanticColor;
    if (iconColorCategories.includes(category)) {
      const semanticColor = getSemanticColor(category, name as string);
      return semanticColor || colorDefault;
    } else {
      // eslint-disable-next-line no-console
      console.error(
        `@athena/forge-icon: semanticColor category used ${category} not found in [ ${iconColorCategories.join(' ')} ]`
      );
    }
  } else {
    switch (variant) {
      case 'default':
        return colorDefault;
      case 'interactive':
        return colorInteractive;
      case 'disabled':
        return colorDisabled;
      case 'inverted':
        return colorInverted;
    }
  }
  return colorDefault;
}

const classes = createBEMHelper({ name: 'icon' });

/** Represents an icon in component form.
 *
 * The component itself has additional metadata applied directly to the function
 * object. This metadata can be used by other components to provide accessability
 * information or style certain icons differently than others.
 */
export type ForgeIconComponent = {
  <Category extends IconColorCategory>(props: IconProps<Category>): JSX.Element;
  /** The name of the component as viewed in dev tools */
  displayName?: string;
  /** When used in a button, its color should be neutral */
  isNeutralInteractive?: boolean;
  /** Whether this is a large or a small icon */
  size?: IconSize;
  /** The default title for the icon */
  title?: string;
};

/** Metadata supplied by metadata.csv */
export interface ForgeIconMetadata {
  name: string;
  usage?: string;
  related?: string;
  size: IconSize;
  isLabelRequired?: boolean;
  isNeutralInteractive?: boolean;
}

/** Higher order function that applies a consistent interface to all generated
 * SVG components.
 */
export function withForgeIconProps(
  SvgComponent: SvgComponentType,
  { name, size: defaultSize, isNeutralInteractive }: ForgeIconMetadata
): ForgeIconComponent {
  // Ensure displayName is TitleCase.
  const displayName = name + defaultSize[0].toUpperCase() + defaultSize.slice(1);
  // Convert PascalCase to Title Case by inserting a space between words
  const defaultTitle = name.replace(/([a-z])([A-Z])/g, '$1 $2');
  const WithForgeIconProps: ForgeIconComponent = ({
    className,
    disabled,
    interactive,
    height: upstreamHeight,
    semanticColor,
    size,
    title = defaultTitle,
    variant = interactive ? (disabled ? 'disabled' : 'interactive') : 'default',
    width: upstreamWidth,
    ...rest
  }) => {
    const [titleId] = useState(nanoid());
    const color = useColor({ variant, semanticColor });
    const heightAndWidth = useMemo(() => {
      const height = upstreamHeight || size;
      const width = upstreamWidth || size;
      /** Conditionally add the width and height props because an explicit
       * `undefined` will override the default width and height.
       */
      return {
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
      };
    }, [size, upstreamHeight, upstreamWidth]);
    /* fi_c_icon exists on SvgComponent by default, but we need to set it in Javascript
     * as well otherwise className will be overwritten by props from components
     * using an icon.
     */
    return (
      <SvgComponent
        {...classes({ modifiers: [variant, defaultSize], extra: className ?? '' })}
        {...heightAndWidth}
        /** Explicitly setting role = `undefined` overrides explicit
         * role="img" in the SvgComponent implementation. It's not appropriate
         * to have an <svg role="img" /> without a title.
         */
        role={title ? 'img' : undefined}
        color={color}
        title={title}
        titleId={title ? titleId : undefined}
        {...rest}
      />
    );
  };
  WithForgeIconProps.title = defaultTitle;
  WithForgeIconProps.displayName = displayName;
  WithForgeIconProps.size = defaultSize;
  WithForgeIconProps.isNeutralInteractive = isNeutralInteractive;
  return WithForgeIconProps;
}
